import { useState, useRef, useEffect } from "react";
import "../styles/navigation.css";
import logo from "../assets/logo-cropped.png";
import hamburger from "../assets/icons/hamburger.svg";


// ─ Dropdown data ─
const NAV_ITEMS = [
  {
    label: "Vehicles",
    href: "https://mycarapp.co.ke/inventory",
    dropdown: [
      {
        heading: "By type",
        links: [
          { label: "Sedans", href: "/inventory?bodyType=Sedan" },
          { label: "SUVs", href: "/inventory?bodyType=SUV" },
          { label: "Pickups", href: "/inventory?bodyType=Pickup" },
          { label: "Crossovers", href: "/inventory?bodyType=Crossover" },
          { label: "Vans", href: "/inventory?bodyType=Van" },
        ],
      },
      {
        heading: "By condition",
        links: [
          { label: "New arrivals", href: "/inventory?condition=New" },
          { label: "Nearly new", href: "/inventory?condition=Nearly+New" },
          { label: "Used", href: "/inventory?condition=Used" },
        ],
      },
      {
        heading: "Popular makes",
        links: [
          { label: "Toyota", href: "/inventory?make=Toyota" },
          { label: "Mazda", href: "/inventory?make=Mazda" },
          { label: "Nissan", href: "/inventory?make=Nissan" },
          { label: "Subaru", href: "/inventory?make=Subaru" },
          { label: "Mercedes-Benz", href: "/inventory?make=Mercedes-Benz" },
          { label: "BMW", href: "/inventory?make=BMW" },
          { label: "Audi", href: "/inventory?make=Audi" },
          { label: "Volkswagen", href: "/inventory?make=Volkswagen" },
          { label: "Honda", href: "/inventory?make=Honda" },
        ],
      },
    ],
  },
  {
    label: "Accessories",
    href: "https://mycarapp.co.ke/accessories",
    dropdown: [
      {
        heading: "Lighting",
        links: [
          { label: "Projector headlights", href: "/accessories/projectors" },
          { label: "LED headlights", href: "/accessories/led-headlights" },
        ],
      },
      {
        heading: "Audio",
        links: [
          { label: "Speakers", href: "/accessories/speakers" },
          { label: "Infotainment systems", href: "/accessories/infotainment" },
        ],
      },
      {
        heading: "Safety & tracking",
        links: [
          { label: "GPS trackers", href: "/accessories/gps-trackers" },
          { label: "Dash cameras", href: "/accessories/dash-cameras" },
          { label: "Reverse cameras", href: "/accessories/reverse-cameras" },
        ],
      },
    ],
  },
  { label: "Spare Parts", href: "https://mycarapp.co.ke/parts" },
  { label: "Find a Mechanic", href: "https://mycarapp.co.ke/breakdown" },
  { label: "Car Hire", href: "https://mycarapp.co.ke/car-hire" },
  { label: "Financing", href: "https://mycarapp.co.ke/financing" },
  { label: "About", href: "https://mycarapp.co.ke/about" },
  { label: "Contact", href: "https://mycarapp.co.ke/contact" },
];

// ─ dropdown icon ─
function ChevronDown({ open }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      aria-hidden="true"
      className={`navbar__chevron ${open ? "navbar__chevron--open" : ""}`}
    >
      <path
        d="M2 3.5L5 6.5L8 3.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─ Dropdown panel ─
function DropdownPanel({ columns }) {
  return (
    <div className="navbar__dropdown">
      {columns.map((col) => (
        <div key={col.heading}>
          <p className="navbar__dropdown-heading">{col.heading}</p>
          <ul className="navbar__dropdown-list">
            {col.links.map((link) => (
              <li key={link.label}>
                <a href={link.href} className="navbar__dropdown-link">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// ─ Main Navbar ─
export default function Navbar() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleMouseEnter(label) {
    clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  }

  function handleMouseLeave() {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 120);
  }

  return (
    <nav
      ref={navRef}
      className="navbar"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="navbar__inner">
        {/* Logo */}
        <a href="/" className="navbar__logo" aria-label="My Car Kenya home">
          <img
            src={logo}
            alt="My Car Kenya Logo"
            className="navbar__logo-image"
          />
        </a>

        {/* Desktop nav */}
        <div className="navbar__menu" role="menubar">
          {NAV_ITEMS.map((item) => {
            const hasDropdown = !!item.dropdown;
            const isOpen = openDropdown === item.label;

            return (
              <div
                key={item.label}
                className="navbar__item"
                onMouseEnter={() => hasDropdown && handleMouseEnter(item.label)}
                onMouseLeave={() => hasDropdown && handleMouseLeave()}
                role="none"
              >
                <a
                  href={item.href || "#"}
                  className={`navbar__link ${isOpen ? "navbar__link--open" : ""}`}
                  role="menuitem"
                  aria-haspopup={hasDropdown ? "true" : undefined}
                  aria-expanded={hasDropdown ? isOpen : undefined}
                  // onClick={(e) => {
                  //   if (hasDropdown) {
                  //     e.preventDefault();
                  //     setOpenDropdown(isOpen ? null : item.label);
                  //   }
                  // }}
                >
                  {item.label}
                  {hasDropdown && <ChevronDown open={isOpen} />}
                </a>

                {hasDropdown && isOpen && (
                  <DropdownPanel columns={item.dropdown} />
                )}
              </div>
            );
          })}
          <a href="/signup" className="navbar__link navbar__link--seller">Sell with us</a>

          {/* Mobile hamburger*/}
          <button
            className="navbar__icon-btn navbar__hamburger"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <img src={hamburger} alt="" width={18} height={18} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="navbar__mobile-drawer">
          {NAV_ITEMS.map((item) => {
            const hasDropdown = !!item.dropdown;
            const isExpanded = mobileExpanded === item.label;

            return (
              <div key={item.label}>
                {hasDropdown ? (
                  <button
                    className="navbar__mobile-item-btn"
                    onClick={() => setMobileExpanded(isExpanded ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown open={isExpanded} />
                  </button>
                ) : (
                  <a
                    href={item.href}
                    className="navbar__mobile-item-btn"
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </a>
                )}

                {hasDropdown && isExpanded && (
                  <div className="navbar__mobile-links">
                    {item.dropdown.map((col) =>
                      col.links.map((link) => (
                        <a
                          key={link.label}
                          href={link.href}
                          className="navbar__mobile-link"
                        >
                          {link.label}
                        </a>
                      )),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </nav>
  );
}
